import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Loader2, Sparkles, TrendingUp } from "lucide-react";
import { api } from "@/contexts/AuthContext";

const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString());

/**
 * CommissionRow — one MTD/YTD block. `data` is `{mtd, ytd, top: [{name, mtd, ytd}]}`
 * from the /api/commissions/snapshot proxy.
 */
export function CommissionCard({ title, icon: Icon, data, groupLabel, mode = "counts", testid }) {
  const [view, setView] = useState("mtd"); // "mtd" | "ytd" for counts, unused for trending
  if (!data) {
    return (
      <Card className="p-4 border-[#E9ECEF] rounded-sm bg-white h-full grid place-items-center" data-testid={testid}>
        <Loader2 size={16} className="animate-spin text-[#D4AF37]" />
      </Card>
    );
  }
  const isTrending = mode === "trending";
  const total = isTrending
    ? data.eom_projection
    : (view === "mtd" ? data.mtd : data.ytd);
  const top = (data.top || []).slice(0, 5);

  return (
    <Card className="p-4 border-[#E9ECEF] rounded-sm bg-white" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={14} className="text-[#D4AF37]" />}
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]">{title}</div>
        </div>
        {!isTrending && (
          <div className="flex rounded-sm overflow-hidden border border-[#E9ECEF]">
            {["mtd", "ytd"].map((v) => (
              <button
                key={v} type="button" onClick={() => setView(v)}
                className={`h-7 px-2 text-[10px] uppercase tracking-[0.16em] font-bold ${
                  view === v ? "bg-[#0B132B] text-white" : "bg-white text-[#6C757D] hover:text-[#0B132B]"
                }`}
                data-testid={`${testid}-${v}`}
              >
                {v}
              </button>
            ))}
          </div>
        )}
        {isTrending && (
          <span className="text-[9px] uppercase tracking-[0.16em] font-bold text-[#0B132B] bg-[#D4AF37]/15 border border-[#D4AF37]/40 rounded-sm px-1.5 py-0.5">
            <TrendingUp size={9} className="inline mr-0.5" /> EOM
          </span>
        )}
      </div>
      <div className="font-display text-3xl font-black text-[#0B132B] font-mono leading-none" data-testid={`${testid}-total`}>
        {fmt(total)}
      </div>
      <div className="text-[10px] text-[#6C757D] mt-1">
        {isTrending
          ? `Projected end-of-month (${data.method || "linear_pace"})`
          : `${view.toUpperCase()} · ${groupLabel}`}
      </div>
      <div className="mt-3 space-y-1">
        {top.length === 0 ? (
          <div className="text-[11px] text-[#6C757D] italic py-2">No activity yet in this window.</div>
        ) : top.map((r, i) => (
          <div
            key={r.id || r.name || i}
            className="flex items-center justify-between text-xs py-1 border-b border-[#F1F3F5] last:border-b-0"
            data-testid={`${testid}-row-${i}`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[9px] uppercase tracking-wider font-bold text-[#D4AF37] w-4 text-right shrink-0">
                {i + 1}
              </span>
              <span className="font-semibold text-[#0B132B] truncate">{r.name || "—"}</span>
            </div>
            <span className="font-mono font-bold text-[#0B132B] shrink-0">
              {isTrending ? fmt(r.projected_eom) : fmt(view === "mtd" ? r.mtd : r.ytd)}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

/**
 * CommissionStub — shown when the proxy returns `unavailable: true`. Keeps
 * the visual footprint identical so the dashboard doesn't jump around
 * while dealercomm's endpoint is still under construction.
 */
export function CommissionUnavailable({ title, icon: Icon, body, testid, reason }) {
  return (
    <Card className="p-4 border-dashed border-2 border-[#E9ECEF] rounded-sm bg-[#F8F9FA]" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={14} className="text-[#0B132B]/40" />}
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#0B132B]/70">{title}</div>
        </div>
        <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-sm px-1.5 py-0.5">
          <Sparkles size={9} className="inline mr-0.5" /> Coming soon
        </span>
      </div>
      <div className="font-display text-2xl font-black text-[#0B132B]/30 font-mono leading-none">—</div>
      <div className="text-[10px] text-[#6C757D] mt-2 leading-tight">{body}</div>
      {reason && (
        <div className="text-[10px] text-[#6C757D] mt-2 leading-tight italic" data-testid={`${testid}-reason`}>
          {reason}
        </div>
      )}
    </Card>
  );
}

/**
 * useCommissionsSnapshot — one fetch on mount. No polling per product spec.
 */
export function useCommissionsSnapshot() {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.post("/commissions/snapshot");
        if (alive) setState({ loading: false, data, error: null });
      } catch (e) {
        if (alive) setState({ loading: false, data: null, error: e?.response?.data?.detail || "Failed to load" });
      }
    })();
    return () => { alive = false; };
  }, []);
  return state;
}
